import React from 'react';

import IconButton from 'components/partials/button/icon-button';
import { Select } from 'components/partials/select/select';

import Icons from 'assets/icons';

interface CategoryOption {
  id: string | number;
  name: string;
}

interface Category {
  category: string;
  options?: CategoryOption[];
}

interface CustomLogicOption<TOptionId extends string | number> {
  value: TOptionId;
  label: string;
}

interface CustomLogicRowProps<TOptionId extends string | number = string | number> {
  category: Category;
  selectedOptionId: TOptionId | null | undefined;
  onSetOption: (
    category: string,
    optionId: TOptionId | null | undefined
  ) => void;
  onRemove: (category: string) => void;
}

const PORTAL_STYLES = (): Record<string, unknown> => ({
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 50 }),
});

const CustomLogicRow = <TOptionId extends string | number = string | number>({
  category,
  selectedOptionId,
  onSetOption,
  onRemove,
}: CustomLogicRowProps<TOptionId>): React.JSX.Element => {
  const isSingleOption = category.options?.length === 1;

  const options = (category.options || []).map<CustomLogicOption<TOptionId>>((opt) => ({
    value: opt.id as TOptionId,
    label: opt.name,
  }));

  const selectedValue = selectedOptionId
    ? options.find((o) => o.value === selectedOptionId) ?? null
    : null;

  return (
    <div
      className="flex items-end gap-2 w-80"
      data-testid={`custom-logic-row-${category.category}`}
    >
      <div className="flex-1 min-w-0">
        <Select
          label={category.category}
          labelInTop
          options={options}
          value={selectedValue ?? undefined}
          onChange={(opt) => {
            if (opt && !Array.isArray(opt)) {
              onSetOption(category.category, opt.value);
            } else {
              onSetOption(category.category, null);
            }
          }}
          placeholder="Select..."
          disabled={isSingleOption}
          menuPortalTarget={document.body}
          extraStyles={PORTAL_STYLES}
          data-testid={`custom-logic-select-${category.category}`}
        />
      </div>
      <IconButton
        icon={<Icons.Delete />}
        variant="reserve-text"
        size="md"
        aria-label="Remove category"
        onClick={() => onRemove(category.category)}
        data-testid={`custom-logic-remove-${category.category}`}
      />
    </div>
  );
};

export default CustomLogicRow;
